import BaseCard from 'Prototype/Level/Card/BaseCard';
import TransformComponent from 'Component/Transform';
import CardSystem from '../CardSystem';
import ITask from 'Utility/Task/ITask';
import { delay } from 'Utility/Task/TaskQueue';
import { StartAnimation, StartAnimationClip, StopAnimation } from '../../Animation/AnimationHelper';
import { AnimationClipEvent } from '../../Animation/AnimationClip';
import Game from 'Game';
import Renderable from 'Component/Renderable';
import GameObject from 'GameObject/GameObject';
import Point from 'Utility/Point';
import SpriteComponent from 'Component/Render/Sprite';
import BonusTextEffect from 'Prototype/Effect/BonusTextEffect';
import Container from 'Prototype/Container';
import { PlaySound } from '../../Audio/AudioHelper';
import { RenderSystemEvent } from '../../Render/RenderSystem';
import ScoreBar from 'Prototype/UI/Level/ScoreBar';
import CardComponent from 'Component/Level/Card';
import { getSingleRemainingCardReward } from 'State/Level/LevelStateHelper';
import { remainingStackRewardStartFactor, remainingStackRewardStartLinearFactor } from 'Constants/Economy';
import LevelHelper from 'Utility/LevelHelper';
import Animator from 'Prototype/Animator';
import { IGameState } from 'State/Game/IGameState';
import { ArrayHelper } from 'Utility/ArrayHelper';
import { getStackCardTargetX } from 'State/Card/CardStateHelper';
import { CardType } from 'Prototype/Level/Card/CardType';
import { MAX_OPENED_CARDS_IN_STACK } from 'Constants/InGame';
import Armature from 'Prototype/Armature';
import { OverlayFadeIn } from 'System/Animation/Clip';
import { LevelHud } from 'System/UI/HUD/LevelHud';
import { GameActions } from 'State/Game/GameActions';

interface IBonusCardLayout {
    x: number;
    y: number;
    angle: number;
    dealSequence: number;
    explodeSequence: number;
}

export default class BonusCardsTask implements ITask<CardSystem> {
    public *execute(system: CardSystem): IterableIterator<number> {
        yield* delay(150);
        const levelState = Game.store.getState().levelState;

        // Wait until all cards finish animating.
        const allCards = system.getAllCards();
        yield* this.waitForAnimationComplete(allCards);

        const inPlayCards = system.getCardsInPlay();
        for (let card of inPlayCards) {
            StartAnimation(card, 'CardFlip', { scale: 1 });
            yield* delay(10);
        }

        system.cardStack.hideLabel();
        yield* this.returnCardsToStack(inPlayCards, system);

        const overlay = LevelHud.instance.getLayout().overlay as GameObject<PIXI.Graphics>;
        const overlayRenderable: Renderable = overlay.getComponent<Renderable>(Renderable);
        overlayRenderable.alpha = 0;
        overlayRenderable.visible = true;
        overlay.emit(RenderSystemEvent.BRING_TO_FRONT);

        system.scene.emit(RenderSystemEvent.BRING_TO_FRONT, overlay.parent);

        const scoreBar = ScoreBar.instance;
        overlay.addChild(scoreBar.root);
        overlay.addChild(system.animator);
        system.animator.emit(RenderSystemEvent.BRING_TO_FRONT);
        scoreBar.root.emit(RenderSystemEvent.BRING_TO_FRONT);

        // Fade in overlay.
        StartAnimationClip(new OverlayFadeIn(overlay));
        yield* delay(250);
        const cardsInStack = [...system.getCardsInStack(), ...inPlayCards];
        // Add all cards to animator.
        for (let i = 0; i < cardsInStack.length; i++) {
            const card = cardsInStack[i];
            StopAnimation(card);

            const transform = card.getComponent(TransformComponent);
            transform.position.set(0);
            // Move to stack and reset position just in case.
            system.stackContainer.addChild(card);
            transform.x = getStackCardTargetX(
                Math.min(i, MAX_OPENED_CARDS_IN_STACK - 1),
                Math.min(cardsInStack.length, MAX_OPENED_CARDS_IN_STACK)
            );

            // Add to animator.
            system.animator.addChild(card);
        }

        const bonusCardLayoutsData: IBonusCardLayout[][] = Game.resource.get('bonus_card_layouts');

        if (cardsInStack.length === 0) return;

        const layouts: IBonusCardLayout[][] = [];
        let cardsInStackCount = cardsInStack.length;
        while (cardsInStackCount > 0) {
            const index = (cardsInStackCount - 1) % bonusCardLayoutsData.length;
            layouts.push(bonusCardLayoutsData[index].slice(0));
            cardsInStackCount -= bonusCardLayoutsData[index].length;
        }

        // Process stack in reverse order.
        cardsInStack.reverse();

        // Build layouts in reverse order.
        layouts.reverse();

        const layoutContainer = GameObject.Instantiate(Container, {
            name: 'layoutContainer',
            pivot: new Point(0, 400),
            relativePosition: new Point(0.5, 0.5),
            scale: new Point(0.9, 0.9),
        });

        system.scene.addGameObject(layoutContainer);

        const isFirstComplete = levelState.completeData.firstComplete;

        // Flat array of cards collected for each card-bonus-explode loop.
        const layoutCardsList = [];

        for (let l = 0; l < layouts.length; l++) {
            const layoutData = layouts[l];
            const layoutCards = cardsInStack.splice(0, layoutData.length);

            layoutCardsList.push(layoutCards);

            // Re-parenting fix - must yield for next frame...
            yield;

            // Create a clone of the layoutCards array because it is not safe to
            // access an array while it is being sorted on Safari.
            const layoutCardsClone = layoutCards.slice(0);

            // Sort by deal-sequence.
            layoutCards.sort(
                (a, b) =>
                    layoutData[layoutCardsClone.indexOf(a)].dealSequence -
                    layoutData[layoutCardsClone.indexOf(b)].dealSequence
            );

            // Slide in each card.
            for (let i = 0; i < layoutCards.length; i++) {
                const card = layoutCards[i];

                const cardComponent = card.getComponent(CardComponent);
                const isPrebooster =
                    cardComponent.type === CardType.canister_pre_booster ||
                    cardComponent.type === CardType.joker_pre_booster;

                // Add each card to layout container.
                layoutContainer.addChild(card);

                let nextCard: BaseCard;
                if (i + 1 < layoutCards.length) {
                    nextCard = layoutCards[i + 1]; // Next card in current layout.
                } else if (l + 1 < layouts.length) {
                    nextCard = cardsInStack[0]; // Top card in stack.
                }

                // Show next card if available.
                if (nextCard) {
                    nextCard.getComponent(Renderable).visible = true;
                }

                card.getComponent(Renderable).visible = true;

                card.getChildByName(BaseCard.CARD_FRONT_NAME).getComponent(SpriteComponent).texture = isFirstComplete
                    ? isPrebooster
                        ? `${cardComponent.type.split('_')[0]}_card_coin`
                        : 'coin-card-bg'
                    : 'cardbonus';

                StartAnimation(card, 'CardSlide', {
                    x: layoutData[i].x,
                    y: layoutData[i].y + l * 15,
                    rotation: (layoutData[i].angle / 180) * Math.PI,
                    duration: 250,
                }).on(AnimationClipEvent.COMPLETE, () => {
                    StartAnimation(card, 'CardFlip', { faceUp: true, isPrebooster });
                });

                yield* delay(150);
            }

            // Sort by explode-sequence.
            layoutCards.sort(
                (a, b) =>
                    layoutData[layoutCardsClone.indexOf(a)].explodeSequence -
                    layoutData[layoutCardsClone.indexOf(b)].explodeSequence
            );
        }

        yield* delay(500);

        // Reverse the order of bonus cards "sections" exploding.
        layoutCardsList.reverse();

        let state: IGameState = Game.store.getState();
        const score: number = state.levelState.score;
        let newScore: number = score;
        const cards = ArrayHelper.flatten(layoutCardsList);
        for (let i = 0; i < cards.length; i++) {
            const card = cards[i];
            const levelCost: number = LevelHelper.getLevelCost(state.levelState.levelNumber);

            const bonusValue = getSingleRemainingCardReward({
                levelCost,
                startFactor: remainingStackRewardStartFactor,
                linearFactor: remainingStackRewardStartLinearFactor,
                card: state.levelState.cardsState.cards[card.id],
                cardIndex: i,
                firstComplete: isFirstComplete,
            });

            const cardTransform = card.getComponent(TransformComponent);
            const cardComponent = card.getComponent(CardComponent);
            card.getComponent(Renderable).visible = false;

            const coin = GameObject.Instantiate(Container, {});

            // TODO: add position when creating the coin
            coin.container.position.copy(cardTransform.position);
            // system.scene.addGameObject(layoutContainer);

            layoutContainer.addChild(coin);

            if (
                (cardComponent.type === CardType.canister_pre_booster ||
                    cardComponent.type === CardType.joker_pre_booster) &&
                isFirstComplete
            ) {
                const cardArmature = GameObject.Instantiate(Armature, {
                    name: 'cardArmature',
                    dragonbonesName: 'level_end_special',
                    armatureName: 'Armature',
                    alpha: 1,
                    playTimes: 1,
                    playOnStart: true,
                });
                coin.addChild(cardArmature);
            } else {
                StartAnimation(coin, 'RemainingCardBonusEffect', {
                    texture: isFirstComplete ? 'coin' : 'star',
                });
            }

            PlaySound('card-reserve-score');

            layoutContainer.addChild(
                GameObject.Instantiate(BonusTextEffect, {
                    text: bonusValue.toString(),
                    anchor: new Point(0.5, 0.5),
                    position: cardTransform.position.clone(),
                })
            );

            // for updating visuals
            system.scene.emit('UpdateScoreProgress', newScore = (score + bonusValue));

            yield* delay(100);
        }

        yield* delay(1000);

        // for updating state
        state = Game.store.getState();
        GameActions.setState(
            {
                ...state,
                levelState: {
                    ...state.levelState,
                    score: newScore
                }
            }
        );
    }

    public *waitForAnimationComplete(cards: BaseCard[]): IterableIterator<number> {
        for (let card of cards) {
            if (card.getComponent(CardComponent).type === CardType.joker_pre_booster) break;
            if (card.getComponent(CardComponent).type === CardType.canister_pre_booster) break;

            const cardComponent = card.getComponent(CardComponent);
            while (cardComponent.animating) yield;
        }
    }

    public *returnCardsToStack(cards: BaseCard[], system: CardSystem): IterableIterator<number> {
        const duration: number = 600;
        const delayStart: number = 10;
        const delayInterval: number = 50;
        const returningCardsAnimator = GameObject.Instantiate(Animator);
        system.animator.addChild(returningCardsAnimator);

        cards.forEach((card: BaseCard) => {
            const modifierContainer = card.getChildByName('modifierContainer');
            if (modifierContainer) {
                modifierContainer.children.forEach(modifier => modifier.destroy());
            }
        });

        StartAnimation(returningCardsAnimator, 'ReturnToStack', {
            stackContainer: system.stackContainer,
            cards,
            duration,
            delayStart,
            delayInterval,
            stackCount: system.getCardsInStack().length,
            system,
        });
        yield* delay(duration + delayStart + cards.length * delayInterval);
    }
}
